/**
 * Utility functions for handling device permissions
 */

/**
 * Request camera permission in a way that's optimized for mobile
 * @returns Promise that resolves with success or rejects with error
 */
export const requestCameraPermission = async () => {
  try {
    // First check if we already have permission to avoid prompting again
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasVideoPermission = devices.some(device => 
      device.kind === 'videoinput' && device.label !== ''
    );
    
    if (hasVideoPermission) {
      return { success: true, already: true };
    }
    
    // Request with minimal constraints for faster permission grant
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    });
    
    // Clean up the stream to avoid keeping camera active
    stream.getTracks().forEach(track => track.stop());
    
    return { success: true, already: false };
  } catch (error) {
    console.error('Camera permission error:', error);
    return Promise.reject({
      success: false,
      error: error,
      errorType: 'camera'
    });
  }
};

/**
 * Request microphone permission in a way that's optimized for mobile
 * @returns Promise that resolves with success or rejects with error
 */
export const requestMicrophonePermission = async () => {
  try {
    // First check if we already have permission to avoid prompting again
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasAudioPermission = devices.some(device => 
      device.kind === 'audioinput' && device.label !== ''
    );
    
    if (hasAudioPermission) {
      return { success: true, already: true };
    }
    
    // Request with minimal constraints for faster permission grant
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true
    });
    
    // Clean up the stream to avoid keeping microphone active
    stream.getTracks().forEach(track => track.stop());
    
    return { success: true, already: false };
  } catch (error) {
    console.error('Microphone permission error:', error);
    return Promise.reject({
      success: false,
      error: error,
      errorType: 'microphone'
    });
  }
};

/**
 * Request location permission in a way that's optimized for mobile
 * Uses low accuracy mode for faster response
 * @returns Promise that resolves with location or rejects with error
 */
export const requestLocationPermission = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({
        success: false,
        error: new Error('Geolocation not supported'),
        errorType: 'location'
      });
      return;
    }

    const timeoutId = setTimeout(() => {
      reject({
        success: false,
        error: new Error('Location request timed out'),
        errorType: 'location',
        errorCode: 3
      });
    }, 10000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          success: true,
          position: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          }
        });
      },
      (error) => {
        clearTimeout(timeoutId);
        console.error('Location permission error:', error);
        reject({
          success: false,
          error: error,
          errorType: 'location',
          errorCode: error.code
        });
      },
      {
        enableHighAccuracy: false, // Low accuracy mode for faster response
        timeout: 8000,
        maximumAge: 60000 // Accept cached positions up to 1 min old
      }
    );
  });
};

/**
 * Get a user-friendly error message for permission errors
 * @param error The error object from permission request
 * @returns Human-readable error message
 */
export const getPermissionErrorMessage = (error: any) => {
  if (!error) return 'Unknown permission error';

  if (error.errorType === 'camera' || error.errorType === 'microphone') {
    if (error.error.name === 'NotAllowedError' || error.error.name === 'PermissionDeniedError') {
      return `${error.errorType === 'camera' ? 'Camera' : 'Microphone'} permission denied. Please check your browser settings.`;
    }
    if (error.error.name === 'NotFoundError') {
      return `No ${error.errorType} found on your device.`;
    }
    if (error.error.name === 'NotReadableError') {
      return `${error.errorType === 'camera' ? 'Camera' : 'Microphone'} is in use by another application.`;
    }
  }

  if (error.errorType === 'location') {
    if (error.errorCode === 1) {
      return 'Location permission denied. Please enable in settings.';
    }
    if (error.errorCode === 2) {
      return 'Location unavailable. Check your connection and GPS.';
    }
    if (error.errorCode === 3) {
      return 'Location request timed out.';
    }
  }

  return `Error accessing ${error.errorType || 'device'}.`;
}; 